import React, { useState, useRef, useEffect, useContext } from 'react';
import { DynamicIcon } from './icons.jsx';
import { ThemeContext } from '../context/ThemeContext.jsx';
import { useNavigate } from 'react-router-dom';
import { useData } from '../hooks/useData.js';
import { Snackbar, Alert, Avatar } from '@mui/material';

import { apiPost } from '../api';

const UserProfileMenu = ({ user, onLogout, token, onProfileClick, onRestartTour }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);
    const { theme } = useContext(ThemeContext);
    const navigate = useNavigate();

    // Fetch Demos (only if visitor role 'r7' or just check everyone)
    // Checking everyone is safer for demo admins too.
    const { data: demos, loading: demosLoading } = useData('/tenant-onboarding/me/demos', token);

    // Snackbar State
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
    const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

    // Close on click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const getInitials = (name) => {
        if (!name) return 'U';
        const parts = name.trim().split(' ');
        if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
        return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    };

    const handleSwitchDemo = async (demoConfigId) => {
        try {
            // Use apiPost to ensure correct Base URL (VITE_API_BASE_URL) and headers are used
            await apiPost('/tenant-onboarding/me/switch-demo', token, { demo_config_id: demoConfigId });

            setSnackbar({ open: true, message: "Switching demo Environment...", severity: 'success' });
            setTimeout(() => {
                // Force reload to new tenant context main page "My Apps"
                window.location.href = '/my-apps';
            }, 500);

        } catch (error) {
            console.error("Switch failed", error);
            setSnackbar({ open: true, message: error.message || "Switch failed", severity: 'error' });
        }
    };


    // Role Mapping for badges
    const ROLE_MAP = {
        'r1': 'Super Admin',
        'r2': 'Platform Admin',
        'r3': 'Partner Admin',
        'r4': 'Tenant Admin',
        'r7': 'User'
    };

    const roleLabel = ROLE_MAP[user?.roleId] || 'User';

    // Determine active demo
    const activeDemoId = demos?.find(d => d.tenant_id === user?.tenantId)?.id;

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                id="user-profile-menu"
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center focus:outline-none rounded-full transition-all"
                title="Account Settings"
            >
                {/* Avatar Circle */}
                <Avatar 
                    src={user?.profile_pic ? (user.profile_pic.startsWith('data:') ? user.profile_pic : `data:image/png;base64,${user.profile_pic}`) : null}
                    sx={{ 
                        width: 40, 
                        height: 40, 
                        fontSize: '1rem', 
                        fontWeight: 'bold',
                        bgcolor: theme.accentColor,
                        color: '#ffffff',
                        '&:hover': { transform: 'scale(1.05)' },
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}
                >
                    {getInitials(user?.name)}
                </Avatar>
            </button>

            {/* Dropdown Menu - Precisely Matched to Reference Image & Compact Size */}
            {isOpen && (
                <div className="absolute right-0 mt-2 w-[290px] bg-white dark:bg-gray-800 rounded-lg shadow-xl border dark:border-gray-700 overflow-hidden z-50 animate-in fade-in zoom-in duration-150">
                    {/* Header: Avatar Left, Info Right */}
                    <div className="p-5">
                        <div className="flex items-center gap-3">
                            <Avatar 
                                src={user?.profile_pic ? (user.profile_pic.startsWith('data:') ? user.profile_pic : `data:image/png;base64,${user.profile_pic}`) : null}
                                sx={{ width: 48, height: 48, bgcolor: theme.accentColor, fontWeight: 'bold', fontSize: '1.2rem' }}
                            >
                                {getInitials(user?.name)}
                            </Avatar>
                            <div className="flex-1 min-w-0">
                                <h4 className="text-[15px] font-bold text-gray-900 dark:text-white leading-tight truncate">
                                    {user?.name || 'User'}
                                </h4>
                                <p className="text-[13px] text-gray-500 dark:text-gray-400 truncate mt-0.5">
                                    {user?.email}
                                </p>
                            </div>
                        </div>

                        {/* Badges side-by-side below */}
                        <div className="flex flex-wrap gap-1.5 mt-3">
                            <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-blue-50 text-blue-600 border border-blue-100 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800">
                                {roleLabel}
                            </span>
                        </div>
                    </div>

                    <div className="border-t dark:border-gray-700 mx-4"></div>

                    {/* Actions Area */}
                    <div className="p-2 py-2">
                        <button
                            onClick={() => {
                                setIsOpen(false);
                                if (onProfileClick) onProfileClick();
                                else navigate('/profile');
                            }}
                            className="w-full flex items-center gap-3 px-4 py-2 text-[14px] font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors group rounded-md"
                        >
                            <DynamicIcon name="UserCircle" className="w-4 h-4 text-gray-400 group-hover:text-primary transition-colors" />
                            My Profile
                        </button>
                        
                        {onRestartTour && (
                            <button
                                onClick={() => {
                                    setIsOpen(false);
                                    onRestartTour();
                                }}
                                className="w-full flex items-center gap-3 px-4 py-2 text-[14px] font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors group rounded-md"
                            >
                                <DynamicIcon name="Sparkles" className="w-4 h-4 text-blue-500 group-hover:text-blue-600 transition-colors" />
                                Take a Quick Tour
                            </button>
                        )}

                        <button
                            onClick={() => {
                                setIsOpen(false);
                                onLogout();
                            }}
                            className="w-full flex items-center gap-3 px-4 py-2 text-[14px] font-medium text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors group rounded-md"
                        >
                            <DynamicIcon name="LogOut" className="w-4 h-4 text-red-500" />
                            Sign out
                        </button>
                    </div>
                </div>
            )}

            <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar}>
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </div>
    );
};

export default UserProfileMenu;
